import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useForecast } from '../../context/useForecast';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import logoUrl from '../../assets/logo.svg';

type NavItem = {
  /** Route path. Absent pour un groupe pur (purement organisationnel). */
  path?: string;
  label: string;
  icon: string;
  children?: NavItem[];
};

const NAV_ITEMS: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: 'lucide:layout-dashboard' },
  { path: '/synthese', label: 'Synthèse', icon: 'lucide:table-2' },
  {
    label: 'Prévisionnel',
    icon: 'lucide:sliders-horizontal',
    children: [
      { path: '/revenus', label: 'Revenus', icon: 'lucide:trending-up' },
      { path: '/charges', label: 'Charges', icon: 'lucide:receipt' },
      { path: '/investissements', label: 'Investissements', icon: 'lucide:line-chart' },
      { path: '/salaires', label: 'Salaires', icon: 'lucide:users' },
      { path: '/notaires', label: 'Notaires', icon: 'lucide:scale' },
      { path: '/dirigeant', label: 'Dirigeants', icon: 'lucide:user-circle' },
    ],
  },
  {
    label: 'Settings',
    icon: 'lucide:settings',
    children: [
      { path: '/fiscalite', label: 'Fiscalité', icon: 'lucide:landmark' },
      { path: '/scenarios', label: 'Scénarios', icon: 'lucide:layers' },
      { path: '/users', label: 'Utilisateurs', icon: 'lucide:user-cog' },
      { path: '/rapports', label: 'Rapports', icon: 'lucide:file-text' },
    ],
  },
];

/** Applati la liste d'items pour le mode sidebar réduite (icônes seulement). */
function flattenNav(items: NavItem[]): NavItem[] {
  return items.flatMap((it) => (it.children ? it.children : [it]));
}

const NAV_INACTIVE = 'text-[#E5EBE1] hover:bg-[#0A3A1A] hover:text-white';
const NAV_ACTIVE = 'bg-white text-[#002108] font-medium';
const DIVIDER = 'border-[#0A3A1A]';

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, user } = useAuth();
  const { saveStatus, forecastName, refreshFromDB } = useForecast();

  // Groupes dépliés (persistés en localStorage). Par défaut : le groupe
  // courant (celui qui contient la route active) est déplié.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-expanded-groups');
      if (stored) return new Set(JSON.parse(stored) as string[]);
    } catch {
      // ignore
    }
    return new Set<string>(['Prévisionnel']);
  });

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      try {
        localStorage.setItem('sidebar-expanded-groups', JSON.stringify(Array.from(next)));
      } catch {
        // ignore
      }
      return next;
    });
  };

  // Auto-ouverture du groupe dont un enfant est actif
  useEffect(() => {
    const groupWithActive = NAV_ITEMS.find(
      (it) => it.children && it.children.some((c) => c.path === location.pathname),
    );
    if (groupWithActive && !expandedGroups.has(groupWithActive.label)) {
      setExpandedGroups((prev) => new Set(prev).add(groupWithActive.label));
    }
  }, [location.pathname, expandedGroups]);

  const userEmail = user?.email ?? '';
  const userName = (user?.user_metadata?.full_name as string | undefined) ?? '';
  const userLabel = userName || userEmail || 'Utilisateur';
  const userInitial = (userName || userEmail || '?').trim().charAt(0).toUpperCase();
  const isProfileActive = location.pathname === '/profile';

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  const saveLabel = saveStatus === 'saving' ? 'Enregistrement...'
    : saveStatus === 'saved' ? 'Enregistré'
    : saveStatus === 'error' ? 'Erreur de sauvegarde'
    : '';

  const saveColor = saveStatus === 'saved' ? 'text-emerald-300'
    : saveStatus === 'error' ? 'text-red-300'
    : 'text-[#B8C4BB]';

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        className={cn(
          'flex flex-col h-screen bg-[#002108] transition-all duration-200 shrink-0 text-white',
          collapsed ? 'w-14' : 'w-56'
        )}
      >
        {/* Logo + nom du scénario */}
        <div className={cn('flex items-center gap-3 px-4 py-3 border-b', DIVIDER, collapsed && 'px-3 justify-center')}>
          <img
            src={logoUrl}
            alt="Logo"
            className="w-8 h-8 object-contain shrink-0"
          />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate leading-tight">{forecastName}</p>
              <p className="text-[10px] text-[#B8C4BB] leading-tight">Scénario actif</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 space-y-0.5 px-2 overflow-y-auto">
          {collapsed
            ? /* Mode réduit : on aplatit la hiérarchie, icônes uniquement */
              flattenNav(NAV_ITEMS).map((item) => {
                const isActive =
                  location.pathname === item.path ||
                  (location.pathname === '/' && item.path === '/dashboard');
                return (
                  <Tooltip key={item.path ?? item.label}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => item.path && navigate(item.path)}
                        className={cn(
                          'flex items-center justify-center w-full h-9 rounded-md transition-colors',
                          isActive ? NAV_ACTIVE : NAV_INACTIVE,
                        )}
                      >
                        <Icon icon={item.icon} className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              })
            : /* Mode déployé : groupes avec sous-onglets */
              NAV_ITEMS.map((item) => {
                // Groupe avec enfants
                if (item.children && item.children.length > 0) {
                  const hasActiveChild = item.children.some((c) => c.path === location.pathname);
                  const isExpanded = expandedGroups.has(item.label) || hasActiveChild;
                  return (
                    <div key={item.label} className="space-y-0.5">
                      <button
                        onClick={() => toggleGroup(item.label)}
                        className={cn(
                          'flex items-center gap-3 w-full h-9 px-3 rounded-md text-sm transition-colors',
                          hasActiveChild
                            ? 'bg-[#0A3A1A] text-white font-medium'
                            : NAV_INACTIVE,
                        )}
                      >
                        <Icon icon={item.icon} className="w-4 h-4 shrink-0" />
                        <span className="truncate flex-1 text-left">{item.label}</span>
                        <Icon
                          icon="lucide:chevron-down"
                          className={cn(
                            'w-3.5 h-3.5 shrink-0 transition-transform duration-200',
                            isExpanded ? 'rotate-0' : '-rotate-90',
                          )}
                        />
                      </button>
                      {isExpanded && (
                        <div className="relative ml-[18px] pl-3 border-l border-[#0A3A1A] space-y-0.5 pt-0.5">
                          {item.children.map((child) => {
                            const childActive = location.pathname === child.path;
                            return (
                              <button
                                key={child.path}
                                onClick={() => child.path && navigate(child.path)}
                                className={cn(
                                  'flex items-center gap-2.5 w-full h-8 px-2.5 rounded-md text-[13px] transition-colors',
                                  childActive ? NAV_ACTIVE : NAV_INACTIVE,
                                )}
                              >
                                <Icon icon={child.icon} className="w-3.5 h-3.5 shrink-0" />
                                <span className="truncate">{child.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Item simple
                const isActive =
                  location.pathname === item.path ||
                  (location.pathname === '/' && item.path === '/dashboard');
                return (
                  <button
                    key={item.path ?? item.label}
                    onClick={() => item.path && navigate(item.path)}
                    className={cn(
                      'flex items-center gap-3 w-full h-9 px-3 rounded-md text-sm transition-colors',
                      isActive ? NAV_ACTIVE : NAV_INACTIVE,
                    )}
                  >
                    <Icon icon={item.icon} className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
        </nav>

        {/* Footer */}
        <div className={cn('border-t p-2 space-y-0.5', DIVIDER)}>
          {/* Bloc utilisateur → accès profil */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate('/profile')}
                  className={cn(
                    'flex items-center justify-center w-full h-9 rounded-md transition-colors',
                    isProfileActive ? NAV_ACTIVE : NAV_INACTIVE,
                  )}
                >
                  <div className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold',
                    isProfileActive ? 'bg-[#002108] text-white' : 'bg-[#0A3A1A] text-white',
                  )}>
                    {userInitial}
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <div className="text-xs font-medium">{userLabel}</div>
                {userName && userEmail && <div className="text-[10px] text-muted-foreground">{userEmail}</div>}
              </TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={() => navigate('/profile')}
              className={cn(
                'flex items-center gap-2.5 w-full px-2 py-1.5 rounded-md transition-colors group',
                isProfileActive ? 'bg-white' : 'hover:bg-[#0A3A1A]',
              )}
              title="Mon profil"
            >
              <div className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0',
                isProfileActive ? 'bg-[#002108] text-white' : 'bg-[#0A3A1A] text-white',
              )}>
                {userInitial}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className={cn(
                  'text-xs font-medium truncate leading-tight',
                  isProfileActive ? 'text-[#002108]' : 'text-white',
                )}>
                  {userName || 'Mon profil'}
                </p>
                {userEmail && (
                  <p className={cn(
                    'text-[10px] truncate leading-tight',
                    isProfileActive ? 'text-[#002108]/70' : 'text-[#B8C4BB]',
                  )}>
                    {userEmail}
                  </p>
                )}
              </div>
              <Icon
                icon="lucide:chevron-right"
                className={cn(
                  'w-3.5 h-3.5 shrink-0 transition-colors',
                  isProfileActive ? 'text-[#002108]' : 'text-[#B8C4BB] opacity-0 group-hover:opacity-100',
                )}
              />
            </button>
          )}

          {!collapsed && saveLabel && (
            <p className={cn('text-xs px-3 py-1', saveColor)}>{saveLabel}</p>
          )}

          {collapsed ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => refreshFromDB()}
                    className="flex items-center justify-center w-full h-9 rounded-md text-[#E5EBE1] hover:bg-[#0A3A1A] hover:text-white transition-colors"
                  >
                    <Icon icon="lucide:refresh-cw" className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Rafraîchir depuis la DB</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => signOut()}
                    className="flex items-center justify-center w-full h-9 rounded-md text-[#E5EBE1] hover:bg-[#0A3A1A] hover:text-red-300 transition-colors"
                  >
                    <Icon icon="lucide:log-out" className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Déconnexion</TooltipContent>
              </Tooltip>
            </>
          ) : (
            <>
              <button
                onClick={() => refreshFromDB()}
                className="flex items-center gap-3 w-full h-9 px-3 rounded-md text-sm text-[#E5EBE1] hover:bg-[#0A3A1A] hover:text-white transition-colors"
              >
                <Icon icon="lucide:refresh-cw" className="w-4 h-4 shrink-0" />
                <span>Rafraîchir</span>
              </button>
              <button
                onClick={() => signOut()}
                className="flex items-center gap-3 w-full h-9 px-3 rounded-md text-sm text-[#E5EBE1] hover:bg-[#0A3A1A] hover:text-red-300 transition-colors"
              >
                <Icon icon="lucide:log-out" className="w-4 h-4 shrink-0" />
                <span>Déconnexion</span>
              </button>
            </>
          )}

          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'flex items-center justify-center w-full h-9 rounded-md text-[#B8C4BB] hover:bg-[#0A3A1A] hover:text-white transition-colors',
              !collapsed && 'gap-3 px-3 justify-start'
            )}
          >
            <Icon
              icon={collapsed ? 'lucide:panel-left-open' : 'lucide:panel-left-close'}
              className="w-4 h-4 shrink-0"
            />
            {!collapsed && <span className="text-sm">Réduire</span>}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
